import { useSyncExternalStore } from 'react';

/**
 * Última ficha de paciente aberta HOJE nesta máquina — o "paciente em
 * atendimento" do botão Agenda ⇄ Ficha do topo. Quem abre a ficha grava;
 * a Topbar lê pra oferecer a volta com um clique depois de ir à agenda.
 *
 * Por máquina (localStorage) e por clínica. Ficha de outro dia não conta:
 * no dia seguinte o botão só aparece depois que uma ficha for aberta.
 */
const CHART_KEY = 'dratento.lastChart';
const EVENT = 'dratento:lastChart';

export interface LastChart {
  clinicId: string;
  patientId: string;
  name: string;
  /** dia (YYYY-MM-DD local) em que a ficha foi aberta */
  day: string;
}

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

let cachedRaw: string | null | undefined;
let cachedValue: LastChart | null = null;

function read(): LastChart | null {
  let raw: string | null = null;
  try { raw = localStorage.getItem(CHART_KEY); } catch { /* storage indisponível */ }
  if (raw === cachedRaw) return cachedValue;
  cachedRaw = raw;
  try {
    const v = raw ? (JSON.parse(raw) as LastChart) : null;
    cachedValue = v && v.patientId && v.clinicId ? v : null;
  } catch {
    cachedValue = null;
  }
  return cachedValue;
}

function subscribe(cb: () => void) {
  window.addEventListener(EVENT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener('storage', cb);
  };
}

export function rememberChart(clinicId: string, patientId: string, name: string) {
  const prev = read();
  if (prev && prev.clinicId === clinicId && prev.patientId === patientId && prev.name === name && prev.day === today()) return;
  try {
    localStorage.setItem(CHART_KEY, JSON.stringify({ clinicId, patientId, name, day: today() }));
  } catch { /* storage indisponível */ }
  window.dispatchEvent(new Event(EVENT));
}

/** Última ficha aberta hoje nesta clínica, ou null. */
export function useLastChart(clinicId: string | null | undefined): LastChart | null {
  const v = useSyncExternalStore(subscribe, read, () => null);
  if (!v || !clinicId || v.clinicId !== clinicId || v.day !== today()) return null;
  return v;
}
